import { useCallback, useMemo, useState } from "react";
import {
  Col,
  Tag,
  Typography,
  Divider,
  Button,
  Tooltip,
  Input,
  message,
} from "antd";
import { CloseOutlined, UserAddOutlined } from "@ant-design/icons";
import Select from "../../../../components/generic/Select";
import { ConversationStatus } from "../../../../constant";
import dayjs from "../../../../utils/dayjs";
import { useDispatch, useSelector } from "react-redux";
import { updateConversation } from "../../../../store/chats/thunks";
import Avatar from "../../../../components/generic/Avatar";
import { getFullName } from "../../../../utils/shared";
import AddMembers from "./AddMembers";
import RemoveMember from "./RemoveMember";
import { selectUser } from "../../../../store/user/selectors";

const { Text } = Typography;

const TicketInfo = ({ selectedChat, tenant_id, setIsShowTicketInfo }) => {
  const user = useSelector(selectUser);

  const [submitting, setSubmitting] = useState(false);
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);

  const dispatch = useDispatch();
  const {
    display_id,
    topic,
    branch,
    status,
    name,
    priority,
    created_at,
    assigned_advisor,
    id,
  } = selectedChat || {};

  const onConversionUpdate = useCallback(
    async (e) => {
      try {
        const isNameUpdate = e?.target?.name === "name";

        if (isNameUpdate && e?.target?.value === name) return;

        setSubmitting(true);

        const result = await dispatch(
          updateConversation({
            payload: {
              conversation_id: id,
              tenant_id,
              ...(isNameUpdate ? { name: e?.target?.value } : { status: e }),
            },
          }),
        );

        if (!result?.error?.message)
          message.success("Conversion Updated Successfully");
      } finally {
        setSubmitting(false);
      }
    },
    [id, tenant_id, name],
  );

  const isAssignedConversionUser = useMemo(
    () => assigned_advisor?.id === user?.user_id || selectedChat?.is_internal,
    [assigned_advisor, user],
  );

  const isCustomer = !!selectedChat?.customer;

  return (
    <Col xs={24} md={7} className="ticket-container h-100">
      {/* Header */}
      <div className="flex  ticket-header align-center justify-space-between">
        <h2>Ticket Information</h2>
        <Button
          icon={<CloseOutlined />}
          onClick={() => setIsShowTicketInfo(false)}
          type="text"
        />
      </div>

      <div className="info-body hide-scrollbar">
        <div className="info-section">
          <div className="info-row">
            <Text type="secondary" className="info-label">
              Ticket Id:
            </Text>
            <Text className="info-value">#{display_id}</Text>
          </div>
          <div className="info-row">
            <Text type="secondary" className="info-label">
              Group Name:
            </Text>

            <Input
              defaultValue={name}
              style={{ width: 180 }}
              variant="underlined"
              onBlur={onConversionUpdate}
              name="name"
              onPressEnter={onConversionUpdate}
            />
          </div>

          {isCustomer && (
            <>
              <div className="info-row">
                <Text type="secondary" className="info-label">
                  Topic:
                </Text>
                <Text className="info-value">{topic?.name}</Text>
              </div>
              <div className="info-row">
                <Text type="secondary" className="info-label">
                  Branch:
                </Text>
                <Text className="info-value">{branch?.name}</Text>
              </div>
            </>
          )}
          <div className="info-row">
            <Text type="secondary" className="info-label">
              Priority:
            </Text>
            <Tag
              color={
                priority === "high"
                  ? "red"
                  : priority === "normal"
                    ? "blue"
                    : "default"
              }
              className="priority-tag"
            >
              {priority?.toUpperCase()}
            </Tag>
          </div>
          <div className="info-row">
            <Text type="secondary" className="info-label">
              Priority:
            </Text>
            <Select
              style={{ width: "120px" }}
              options={Object.values(ConversationStatus).map((value) => ({
                value,
              }))}
              variant="borderless"
              defaultValue={status}
              onChange={onConversionUpdate}
              loading={submitting}
              disabled={submitting}
            />
          </div>
          <div className="info-row">
            <Text type="secondary" className="info-label">
              Created:
            </Text>
            <Text className="info-value">
              {dayjs(created_at).format("DD MMM YYYY, hh:mm A")}
            </Text>
          </div>
          {isCustomer && (
            <div className="info-row">
              <Text type="secondary" className="info-label">
                Assigned Advisor:
              </Text>
              <Text className="info-value">
                {assigned_advisor?.name || "Unassigned"}
              </Text>
            </div>
          )}
        </div>

        {isCustomer && (
          <>
            <Divider className="ticket-divider" />
            <div className="info-section">
              <div className="flex justify-space-between">
                <Text strong className="section-title">
                  Customers
                </Text>
              </div>
              <div className="members-list">
                {selectedChat?.customers?.map((c) => (
                  <div key={c.id} className={`member-item`}>
                    <Avatar user={c} size={38} />

                    <div className="member-details">
                      <div className="member-name ellipsis-text">
                        {getFullName(c)}
                      </div>
                      <Tooltip
                        title={`${c.phone_country_code}-${c.phone}  ${c.email || "Email not Found"}`}
                      >
                        <div className="member-email ellipsis-text">
                          {`${c.phone_country_code}-${c.phone}`} |{" "}
                          {c.email || "Email not Found"}
                        </div>
                      </Tooltip>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        <Divider className="ticket-divider" />
        <div className="info-section">
          <div className="flex justify-space-between header">
            <Text strong className="section-title">
              Members
            </Text>
            {isAssignedConversionUser && (
              <Button
                icon={<UserAddOutlined />}
                shape="circle"
                type="primary"
                size="small"
                onClick={() => setShowAddMemberModal(true)}
              />
            )}
          </div>
          <div className="members-list">
            {selectedChat?.members?.map((member) => (
              <div key={member.id} className="member-item ">
                <Avatar user={member} size={38} />

                <div className="member-details">
                  <div className="member-name ellipsis-text">
                    {getFullName(member)}
                  </div>
                  <div className="member-email ellipsis-text">
                    {member.email}
                  </div>
                </div>
                {isAssignedConversionUser &&
                  member?.id !== assigned_advisor?.id &&
                  user?.user_id !== member?.id &&
                  (!selectedChat?.is_internal ||
                    selectedChat?.members?.length > 2) && (
                    <RemoveMember
                      user={member}
                      conversation_id={selectedChat?.id}
                    />
                  )}
              </div>
            ))}
          </div>
        </div>
      </div>
      {showAddMemberModal && (
        <AddMembers
          onCancel={() => setShowAddMemberModal(false)}
          selectedChat={selectedChat}
        />
      )}
    </Col>
  );
};

export default TicketInfo;
